import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-971">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-972">
        <Card data-id="element-973">
          <CardContent className="p-5" data-id="element-974">
            <div className="flex items-start gap-3" data-id="element-975">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-976">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-977" />
              </div>
              <div className="min-w-0" data-id="element-978">
                <p className="text-sm text-gray-500 font-medium" data-id="element-979">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-980">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-981">
          <CardContent className="p-5" data-id="element-982">
            <div className="flex items-start gap-3" data-id="element-983">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-984">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-985" />
              </div>
              <div className="min-w-0" data-id="element-986">
                <p className="text-sm text-gray-500 font-medium" data-id="element-987">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-988">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-989">
          <CardContent className="p-5" data-id="element-990">
            <div className="flex items-start gap-3" data-id="element-991">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-992">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-993" />
              </div>
              <div className="min-w-0" data-id="element-994">
                <p className="text-sm text-gray-500 font-medium" data-id="element-995">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-996">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-997">
        {/* Peak Day */}
        <Card data-id="element-998">
          <CardContent className="p-5" data-id="element-999">
            <div className="flex items-center gap-2 mb-3" data-id="element-1000">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-1001">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-1002" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1003">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-1004">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-1005">
                    <div className="flex items-center gap-2" data-id="element-1006">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1007">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-1008">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-1009">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-1010">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-1011">
          <CardContent className="p-5" data-id="element-1012">
            <div className="flex items-center gap-2 mb-3" data-id="element-1013">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-1014">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-1015" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1016">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-1017">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-1018">
                    <div className="flex items-center gap-2" data-id="element-1019">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1020">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-1021">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-1022">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-1023">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-1024">
          <CardContent className="p-5" data-id="element-1025">
            <div className="flex items-center gap-2 mb-3" data-id="element-1026">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-1027">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-1028" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1029">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-1030">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-1031">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1032">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-1033">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-1034">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-1035">
          <CardContent className="p-5" data-id="element-1036">
            <div className="flex items-center gap-2 mb-3" data-id="element-1037">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-1038">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-1039" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-1040">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-1041">
              <p className="text-2xl font-bold text-blue-600" data-id="element-1042">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-1043">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-1044">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-1045">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}